import pkg from "../../../package.json";

// Task #993 (RN port — server-side additive prep). Every outbound API
// request from the web client advertises its name + package version via
// the `X-Client` header so the server can attribute long-lived deployed
// binaries when we add the React Native clients. Format matches the
// React Native port RFC §6 — `<client>/<version>`.
//
// All client-side fetch sites (TanStack Query via `queryClient.ts`, plus
// direct `fetch` calls in `AuthService.ts`) import from this module so
// the header value lives in exactly one place.
const APP_VERSION = (pkg as { version?: string }).version || "dev";

export const X_CLIENT_HEADER_NAME = "X-Client";
export const X_CLIENT_HEADER_VALUE = `kudoz-web/${APP_VERSION}`;

export function getClientHeader(): Record<string, string> {
  return { [X_CLIENT_HEADER_NAME]: X_CLIENT_HEADER_VALUE };
}

// Task #993: install a one-time global fetch wrapper so EVERY same-origin
// `/api/...` request the web app makes carries the `X-Client` header —
// not just the ones routed through `queryClient.ts` or `AuthService.ts`.
// There are ~160 direct `fetch('/api/...')` call sites across hooks,
// services, partner/admin pages, and components; rather than touch each
// one (and risk drift forever), we patch the global once at app boot.
//
// Rules:
//   - Only patched in the browser.
//   - Only applies to same-origin (or scheme-relative) `/api/...` URLs.
//   - Never overrides an explicit caller-supplied X-Client header.
//   - Idempotent: a second call is a no-op.
//
// `installClientHeaderInterceptor` is invoked exactly once from
// `client/src/main.tsx` before the React tree mounts.
let installed = false;

function isApiRequest(input: RequestInfo | URL): boolean {
  try {
    const raw =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : "";
    if (!raw) return false;
    if (raw.startsWith("/api/") || raw === "/api") return true;
    // Same-origin absolute URLs (e.g. `${location.origin}/api/...`).
    if (
      typeof window !== "undefined" &&
      raw.startsWith(window.location.origin)
    ) {
      const path = raw.slice(window.location.origin.length);
      return path.startsWith("/api/") || path === "/api";
    }
    return false;
  } catch {
    return false;
  }
}

function headersHasXClient(headers: HeadersInit | undefined): boolean {
  if (!headers) return false;
  if (headers instanceof Headers) return headers.has(X_CLIENT_HEADER_NAME);
  if (Array.isArray(headers)) {
    return headers.some(
      ([k]) => k?.toLowerCase() === X_CLIENT_HEADER_NAME.toLowerCase()
    );
  }
  return Object.keys(headers).some(
    (k) => k.toLowerCase() === X_CLIENT_HEADER_NAME.toLowerCase()
  );
}

export function installClientHeaderInterceptor(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function")
    return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = function patchedFetch(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    try {
      if (!isApiRequest(input)) return originalFetch(input, init);

      // Request object path. Per the Fetch spec, when `fetch(request, init)`
      // is called and `init.headers` is provided, those headers REPLACE the
      // request's own headers — so we cannot safely inject only into the
      // Request and pass `init` through unchanged. We merge in priority order
      // (Request headers → init.headers → injected) into a single Headers,
      // then drop init.headers entirely.
      if (input instanceof Request) {
        const explicit =
          input.headers.has(X_CLIENT_HEADER_NAME) ||
          headersHasXClient(init?.headers);
        if (explicit) return originalFetch(input, init);
        const merged = new Headers(input.headers);
        if (init?.headers) {
          new Headers(init.headers).forEach((value, key) =>
            merged.set(key, value)
          );
        }
        merged.set(X_CLIENT_HEADER_NAME, X_CLIENT_HEADER_VALUE);

        const { headers: _drop, ...initWithoutHeaders } = init ?? {};
        return originalFetch(
          new Request(input, { headers: merged }),
          initWithoutHeaders
        );
      }

      // String / URL path: merge into init.headers.
      if (headersHasXClient(init?.headers)) return originalFetch(input, init);
      const headers = new Headers(init?.headers);
      headers.set(X_CLIENT_HEADER_NAME, X_CLIENT_HEADER_VALUE);
      return originalFetch(input, { ...(init ?? {}), headers });
    } catch {
      // Never let header injection break a real request.
      return originalFetch(input, init);
    }
  };
}
